import React, { useState, useEffect } from 'react'
import { X, Eye, DollarSign, Clock } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { formatCentsAsCurrency } from '../utils/currency'
import { getMainImageUrl } from '../utils/imageUtils'
import { apiService } from '../services/api'

interface BidNotificationData {
  itemId: string
  bid: {
    id: string
    user: {
      id: string
      displayName: string
      profileImageUrl?: string
    }
    item: {
      id: string
      title: string
      images?: string[]
      imageUrl?: string
      mainImageIndex?: number
    }
  }
  amount: number
  item: {
    id: string
    currentPrice: number
    startPrice: number
  }
}

interface StackedNotification extends BidNotificationData {
  id: string
  createdAt: number
}

interface BidNotificationStackProps {
  notifications: StackedNotification[]
  onClose: (id: string) => void
}

export const BidNotificationStack: React.FC<BidNotificationStackProps> = ({
  notifications,
  onClose
}) => {
  const navigate = useNavigate()
  const [currentTime, setCurrentTime] = useState(Date.now())

  // Update current time every second for countdown
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now())
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  if (notifications.length === 0) return null

  const handleViewItem = async (notification: StackedNotification) => {
    try {
      // Get item details to find the auction URL
      const itemResponse = await apiService.getItem(notification.itemId)
      if (itemResponse.success && itemResponse.data?.auctionId) {
        const auctionResponse = await apiService.get(`/auctions/${itemResponse.data.auctionId}`)
        if (auctionResponse.success && auctionResponse.data?.url) {
          navigate(`/${auctionResponse.data.url}/${notification.itemId}`)
          onClose(notification.id)
          return
        }
      }
      
      // Fallback: navigate to item page using /i/:id format
      navigate(`/i/${notification.itemId}`)
      onClose(notification.id)
    } catch (error) {
      // Fallback: navigate to item page using /i/:id format
      navigate(`/i/${notification.itemId}`)
      onClose(notification.id)
    }
  }

  const getNotificationImageUrl = (notification: StackedNotification): string | undefined => {
    // Create a mock item object to use with getMainImageUrl
    const mockItem = {
      images: notification.bid.item.images,
      imageUrl: notification.bid.item.imageUrl,
      mainImageIndex: notification.bid.item.mainImageIndex
    }
    return getMainImageUrl(mockItem as any)
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 space-y-2">
      {notifications.map((notification, index) => {
        // Calculate opacity: newest (bottom) = 100%, each one up gets 20% more transparent
        const opacity = 1 - (notifications.length - 1 - index) * 0.2
        
        // Calculate time left for the newest notification (bottom one)
        const isNewest = index === notifications.length - 1
        const timeElapsed = currentTime - notification.createdAt
        const timeLeft = Math.max(0, Math.ceil((30000 - timeElapsed) / 1000))

        return (
          <div
            key={notification.id}
            className="transition-all duration-300 ease-in-out transform translate-y-0 scale-100"
            style={{ opacity }}
          >
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 p-4 max-w-sm w-80 relative overflow-hidden">
              {/* Progress bar - show on all notifications */}
              <div 
                className="absolute top-0 left-0 h-1 bg-blue-500 transition-all duration-1000 ease-linear"
                style={{ width: `${(timeLeft / 30) * 100}%` }} 
              />
              
              {/* Close button */}
              <button
                onClick={() => onClose(notification.id)}
                className="absolute top-2 right-2 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <X className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              </button>

              {/* Content */}
              <div className="pr-6">
                {/* Header */}
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-1.5 bg-green-100 dark:bg-green-900 rounded-full">
                    <DollarSign className="h-4 w-4 text-green-600 dark:text-green-400" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
                    New Bid Placed!
                  </h3>
                </div>

                {/* Bid details with image */}
                <div className="space-y-3">
                  {/* Item info with image */}
                  <div className="flex items-start gap-3">
                    {/* Item image */}
                    <div className="flex-shrink-0">
                      {getNotificationImageUrl(notification) ? (
                        <img
                          src={getNotificationImageUrl(notification)}
                          alt={notification.bid.item.title}
                          className="w-12 h-12 rounded-lg object-cover border border-gray-200 dark:border-gray-600"
                          onError={(e) => {
                            // Hide image if it fails to load
                            e.currentTarget.style.display = 'none'
                          }}
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center border border-gray-200 dark:border-gray-600">
                          <DollarSign className="h-6 w-6 text-gray-400" />
                        </div>
                      )}
                    </div>

                    {/* Item details */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-600 dark:text-gray-300 truncate">
                        <span className="font-medium">{notification.bid.user.displayName}</span> bid on
                      </p>
                      <p className="font-semibold text-gray-900 dark:text-white text-sm truncate">
                        {notification.bid.item.title}
                      </p>
                    </div>
                  </div>

                  {/* Price and timer */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">New Price</p>
                      <p className="text-lg font-bold text-green-600 dark:text-green-400">
                        {formatCentsAsCurrency(notification.amount)}
                      </p>
                    </div>
                    {/* Show timer on all notifications */}
                    <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                      <Clock className="h-3 w-3" />
                      <span>{timeLeft}s</span>
                    </div>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => onClose(notification.id)}
                    className="flex-1 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => handleViewItem(notification)}
                    className="flex-1 px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center gap-1"
                  >
                    <Eye className="h-4 w-4" />
                    View
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default BidNotificationStack
